// src/pages/GatePasses/parts/types.ts
//
// Shared types + mappers for the Gate Pass admin screens.
// Mirrors models.GatePass / models.StudentMovement in the Go backend
// (models/gate.go) and the extra fields GetGatePasses adds
// (student_name, sp_student_id).

export type GatePassStatus =
  | 'pending'
  | 'approved'
  | 'out'
  | 'returned'
  | 'completed'
  | 'rejected'
  | 'cancelled';

export type PassType = 'early_departure' | 'short_leave';

export type ApprovalMethod = 'admin' | 'otp';

export interface ApiGatePassOTP {
  channel: string;
  expires_at: string;
  attempts: number;
  verified: boolean;
  verified_by?: string | null;
  verified_at?: string | null;
}

// Raw row as the API returns it. The list endpoint aggregates into bson.M,
// so its rows carry `_id`; GET /gate-passes/:id decodes into the struct and
// returns `id`. The mapper accepts either.
export interface ApiGatePass {
  id?: string;
  _id?: string;
  student_id: string;
  student_session_id: string;
  date: string;
  pass_type: PassType;
  status: GatePassStatus;
  reason: string;
  picked_up_by: string;
  relation: string;
  contact_no: string;
  approval_method?: ApprovalMethod | '';
  approved_by: string | null;
  approved_at: string | null;
  otp: ApiGatePassOTP | null;
  expected_return_at: string | null;
  out_movement_id: string | null;
  in_movement_id: string | null;
  rejected_at?: string | null;
  cancelled_at?: string | null;
  cancel_reason?: string;
  created_at: string;
  updated_at: string;
  student_name?: string;
  sp_student_id?: string;
  out_at?: string | null; // list only: when the student actually went out
  in_at?: string | null; // list only: when the student actually came back
}

// camelCase shape used by the components
export interface GatePassRecord {
  id: string;
  studentId: string;
  studentName: string;
  spStudentId: string;
  date: string;
  passType: PassType;
  status: GatePassStatus;
  reason: string;
  pickedUpBy: string;
  relation: string;
  contactNo: string;
  approvalMethod: ApprovalMethod | '';
  approvedAt: string | null;
  otp: {
    channel: string;
    expiresAt: string;
    attempts: number;
    verified: boolean;
    verifiedAt: string | null;
  } | null;
  expectedReturnAt: string | null;
  outMovementId: string | null;
  inMovementId: string | null;
  outAt: string | null;
  inAt: string | null;
  rejectedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string;
  createdAt: string;
  updatedAt: string;
}

export interface GatePassPagination {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
}

export const mapApiGatePass = (row: ApiGatePass): GatePassRecord => ({
  id: row.id || row._id || '',
  studentId: row.student_id,
  studentName: row.student_name || '',
  spStudentId: row.sp_student_id || '',
  date: row.date || '',
  passType: row.pass_type,
  status: row.status,
  reason: row.reason || '',
  pickedUpBy: row.picked_up_by || '',
  relation: row.relation || '',
  contactNo: row.contact_no || '',
  approvalMethod: row.approval_method || '',
  approvedAt: row.approved_at || null,
  otp: row.otp
    ? {
        channel: row.otp.channel || '',
        expiresAt: row.otp.expires_at || '',
        attempts: row.otp.attempts ?? 0,
        verified: !!row.otp.verified,
        verifiedAt: row.otp.verified_at || null,
      }
    : null,
  expectedReturnAt: row.expected_return_at || null,
  outMovementId: row.out_movement_id || null,
  inMovementId: row.in_movement_id || null,
  outAt: row.out_at || null,
  inAt: row.in_at || null,
  rejectedAt: row.rejected_at || null,
  cancelledAt: row.cancelled_at || null,
  cancelReason: row.cancel_reason || '',
  createdAt: row.created_at || '',
  updatedAt: row.updated_at || '',
});

// Student row from GET /students/search (one row per student_session).
export interface StudentSearchResult {
  studentId: string;
  name: string;
  spStudentId: string;
  className: string;
  section: string;
  rollNo: number | null;
  fatherName: string;
  fatherNo: string;
  motherName: string;
  motherNo: string;
  photoUrl: string;
}

// Same mapping as StudentsListDashboard: student_id is Student._id; _id on
// these rows is the StudentSession id, which gate passes don't use.
export const mapSearchStudent = (row: any): StudentSearchResult => ({
  studentId: row.student_id ?? row.id,
  name: row.name || '',
  spStudentId: row.sp_student_id || '',
  className: row.class_name || '',
  section: row.section || '',
  rollNo: typeof row.roll_no === 'number' ? row.roll_no : null,
  fatherName: row.father_name || '',
  fatherNo: row.father_no || '',
  motherName: row.mother_name || '',
  motherNo: row.mother_no || '',
  photoUrl: row.profile_picture?.url || '',
});

export const STATUS_META: Record<
  GatePassStatus,
  { label: string; color: string; bg: string; border: string; icon: string }
> = {
  pending: {
    label: 'Pending',
    color: '#b45309',
    bg: 'rgba(245,158,11,.12)',
    border: 'rgba(245,158,11,.3)',
    icon: 'pi pi-clock',
  },
  approved: {
    label: 'Approved',
    color: '#1d4ed8',
    bg: 'rgba(59,130,246,.10)',
    border: 'rgba(59,130,246,.25)',
    icon: 'pi pi-check-circle',
  },
  out: {
    label: 'Out on leave',
    color: '#7c3aed',
    bg: 'rgba(124,58,237,.10)',
    border: 'rgba(124,58,237,.25)',
    icon: 'pi pi-sign-out',
  },
  returned: {
    label: 'Returned',
    color: '#15803d',
    bg: 'rgba(22,163,74,.10)',
    border: 'rgba(22,163,74,.25)',
    icon: 'pi pi-sign-in',
  },
  completed: {
    label: 'Gone home',
    color: '#475569',
    bg: 'rgba(100,116,139,.10)',
    border: 'rgba(100,116,139,.25)',
    icon: 'pi pi-home',
  },
  rejected: {
    label: 'Rejected',
    color: '#b91c1c',
    bg: 'rgba(239,68,68,.10)',
    border: 'rgba(239,68,68,.25)',
    icon: 'pi pi-times-circle',
  },
  cancelled: {
    label: 'Cancelled',
    color: '#6b7280',
    bg: 'rgba(107,114,128,.10)',
    border: 'rgba(107,114,128,.25)',
    icon: 'pi pi-ban',
  },
};

export const OVERDUE_META = {
  label: 'Overdue',
  color: '#b91c1c',
  bg: 'rgba(239,68,68,.10)',
  border: 'rgba(239,68,68,.3)',
  icon: 'pi pi-exclamation-triangle',
};

// Each pass type has its own accent so the two are never confused on screen:
// orange = going home for the day, teal = stepping out and coming back.
export const PASS_TYPE_META: Record<
  PassType,
  {
    label: string;
    plural: string;
    icon: string;
    hint: string;
    color: string;
    bg: string;
    border: string;
    gradient: string;
  }
> = {
  early_departure: {
    label: 'Early departure',
    plural: 'Early departures',
    icon: 'pi pi-home',
    hint: 'Leaves for the rest of the day',
    color: '#c2410c',
    bg: 'rgba(249,115,22,.08)',
    border: 'rgba(249,115,22,.3)',
    gradient: 'linear-gradient(135deg, #c2410c 0%, #f97316 100%)',
  },
  short_leave: {
    label: 'Short leave',
    plural: 'Short leaves',
    icon: 'pi pi-replay',
    hint: 'Goes out and comes back the same day',
    color: '#0f766e',
    bg: 'rgba(20,184,166,.08)',
    border: 'rgba(20,184,166,.3)',
    gradient: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
  },
};

// Only the statuses a pass of that type can actually reach. An early
// departure is never "out" or "returned". A short leave is created with the
// student already out, so it is only ever "out" or "returned".
export const STATUSES_BY_TYPE: Record<PassType, GatePassStatus[]> = {
  early_departure: ['pending', 'approved', 'completed', 'rejected', 'cancelled'],
  short_leave: ['out', 'returned'],
};

// Type-aware status label — "approved" means something slightly different
// for each type, and the wording on screen should say exactly that.
export const statusLabel = (status: GatePassStatus, passType: PassType): string => {
  if (status === 'approved') return passType === 'short_leave' ? 'Ready to go out' : 'Approved · ready to leave';
  if (status === 'returned') return 'Back in school';
  if (status === 'completed' && passType === 'early_departure') return 'Gone home';
  return STATUS_META[status].label;
};

export const APPROVAL_METHOD_LABEL: Record<ApprovalMethod, string> = {
  admin: 'Approved by school',
  otp: 'Approved by parent (OTP)',
};

// Short leave only: the student goes out on their own (no name/contact).
export const RELATION_ALONE = 'Alone';

// Quick "back by" picks for a short leave, in minutes from now.
export const RETURN_QUICK_PICKS = [
  { label: '+15 min', minutes: 15 },
  { label: '+30 min', minutes: 30 },
  { label: '+1 hr', minutes: 60 },
  { label: '+2 hr', minutes: 120 },
];

export const RELATION_OPTIONS = [
  'Father',
  'Mother',
  'Guardian',
  'Grandparent',
  'Uncle',
  'Aunt',
  'Sibling',
  'Other',
];

export const REASON_OPTIONS = [
  'Medical',
  'Doctor appointment',
  'Family function',
  'Family emergency',
  'Personal work',
  'Other',
];

// A short leave still out past its expected return time.
export const isOverdue = (r: GatePassRecord, now: Date = new Date()): boolean =>
  r.status === 'out' &&
  r.passType === 'short_leave' &&
  !!r.expectedReturnAt &&
  new Date(r.expectedReturnAt).getTime() < now.getTime();

export const getInitials = (name: string): string =>
  name
    ?.trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('') || '?';

// `date` is a plain IST "YYYY-MM-DD" string; the *_at fields are Go
// time.Time values serialised as ISO timestamps — they format differently.
export const formatPassDate = (dateStr: string): string => {
  if (!dateStr) return '—';
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// "early", "on time" or "12 min late" for a return against its back-by time.
export const returnTiming = (inAt: string | null, expectedAt: string | null): { label: string; late: boolean } | null => {
  if (!inAt || !expectedAt) return null;
  const diffMin = Math.round((new Date(inAt).getTime() - new Date(expectedAt).getTime()) / 60_000);
  if (isNaN(diffMin)) return null;
  if (diffMin <= 0) return { label: diffMin < -2 ? 'early' : 'on time', late: false };
  const late = diffMin >= 60 ? `${Math.floor(diffMin / 60)} hr ${diffMin % 60} min` : `${diffMin} min`;
  return { label: `${late} late`, late: true };
};

export const formatTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// Local YYYY-MM-DD — avoids the UTC shift you get from toISOString().
export const toDateParam = (d: Date | null): string => {
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};